import React, { useState, useEffect, useMemo } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { API_BASE_URL, enhancedFetch } from '../services/api';

const FileDownloader = ({ token, onUnauthorized, setError, setSuccess }) => {
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setErrorFlg] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  

  const handleError = (error) =>{
    setError(error);
    setErrorFlg(error);
  }

  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const [filesPerPage] = useState(10);

  // 获取所有文件列表
  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      handleError(null);
      
      try {
        const response = await enhancedFetch(`${API_BASE_URL}/scripts/files/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }, onUnauthorized);
        if (!response.ok) {
          throw new Error('获取文件列表失败');
        }
        const data = await response.json();
        setFiles(data);
        setSelectedFiles([]);
        setCurrentPage(1);
      } catch (err) {
        handleError('加载文件失败: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [token, onUnauthorized]);

  useEffect(() => {
    if(error){
      const timer = setTimeout(() => {
        handleError(null);
      }, 5000)
      return () => clearTimeout(timer)
    }
  },[error])

  // 根据搜索词过滤文件
  const filteredFiles = useMemo(() => {
    if (!searchTerm) return files;
    
    let term = searchTerm.toLowerCase();
    if (term.startsWith("0x")) {
      term = term.substring(2)
    }
    return files.filter(file => 
      file.pubkey.toLowerCase().includes(term) || 
      file.filename.toLowerCase().includes(term)
    );
  }, [files, searchTerm]);

  // 计算文件列表分页数据
  const indexOfLastFile = currentPage * filesPerPage;
  const indexOfFirstFile = indexOfLastFile - filesPerPage;
  const currentFiles = filteredFiles.slice(indexOfFirstFile, indexOfLastFile);
  const totalFilePages = Math.ceil(filteredFiles.length / filesPerPage);

  // 处理文件选择
  const handleFileSelect = (filename) => {
    if (selectedFiles.includes(filename)) {
      setSelectedFiles(selectedFiles.filter(f => f !== filename));
    } else {
      setSelectedFiles([...selectedFiles, filename]);
    }
  };

  // 处理文件全选/取消全选
  const handleSelectAllFiles = () => {
    if (selectedFiles.length === currentFiles.length) {
      // 只取消当前页面的选中
      const currentPageFileNames = currentFiles.map(f => f.filename);
      setSelectedFiles(selectedFiles.filter(f => !currentPageFileNames.includes(f)));
    } else {
      // 选中当前页面的所有文件
      const currentPageFileNames = currentFiles.map(f => f.filename);
      const newSelected = [...selectedFiles];
      currentPageFileNames.forEach(filename => {
        if (!newSelected.includes(filename)) {
          newSelected.push(filename);
        }
      });
      setSelectedFiles(newSelected);
    }
  };

  // 处理跨页全选
  const handleSelectAllPages = () => {
    if (selectedFiles.length === filteredFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(filteredFiles.map(f => f.filename));
    }
  };

  // 下载单个文件
  const downloadFile = async (filename) => {
    try {
      // 找到文件对应的公钥
      const file = files.find(f => f.filename === filename);
      const pubkey = file ? file.pubkey : 'unknown';
      
      const response = await enhancedFetch(`${API_BASE_URL}/scripts/download/${filename}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }, onUnauthorized);
      if (!response.ok) {
        throw new Error('下载文件失败');
      }
      
      const data = await response.json();
      
      // Create a blob from the JSON data
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json'});
      
      // Generate filename with pubkey
      const fileExtension = filename.split('.').pop();
      const baseName = filename.replace(`.${fileExtension}`, '');
      const newFilename = `${pubkey.substring(0, 8)}_${baseName}.json`;
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = newFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      handleError('下载文件失败: ' + err.message);
    }
  };

  // 批量下载文件，打包成一个压缩包
  const downloadBatchAsZip = async () => {
    if (selectedFiles.length === 0) {
      handleError('请选择至少一个文件进行下载');
      return;
    }

    try {
      setLoading(true);
      handleError(null);
      
      // 创建ZIP文件
      const zip = new JSZip();
      
      // 添加每个文件到ZIP中
      for (const filename of selectedFiles) {
        const file = files.find(f => f.filename === filename);
        const pubkey = file ? file.pubkey : 'unknown';
        
        try {
          const response = await enhancedFetch(`${API_BASE_URL}/scripts/download/${filename}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }, onUnauthorized);
          if (response.ok) {
            const data = await response.json();
            
            // 以公钥命名文件
            const fileExtension = filename.split('.').pop();
            const baseName = filename.replace(`.${fileExtension}`, '');
            const newFilename = `${pubkey.substring(0, 8)}_${baseName}.json`;
            
            // 添加文件到ZIP
            zip.file(newFilename, JSON.stringify(data, null, 2));
          }
        } catch (err) {
          console.error(`添加文件 ${filename} 到压缩包失败:`, err);
        }
      }
      
      // 生成ZIP文件并下载
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `script_results_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.zip`);
      
    } catch (err) {
      handleError('下载文件失败: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 格式化时间戳
  const formatTime = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  // 处理页面切换
  const paginateFiles = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // 处理搜索输入变化
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // 重置到第一页
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>文件下载</h2>
        <p>下载脚本执行结果文件</p>
      </div>

      <div>
        <div className="files-header">
          <h3>所有文件 (共 {filteredFiles.length} 个)</h3>
          
          {/* 搜索框 */}
          <div className="search-container">
            <div className="form-group">
              <label htmlFor="pubkey-search">按公钥或文件名搜索:</label>
              <input
                type="text"
                id="pubkey-search"
                placeholder="输入公钥或文件名进行筛选..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>
          </div>
        </div>
        
        {loading && filteredFiles.length === 0 ? (
          <div className="loading-placeholder">
            <p>正在加载文件...</p>
          </div>
        ) : (
          <>
            {filteredFiles.length > 0 && (
              <div className="files-content">
                <div className="files-toolbar">
                  <div className="toolbar-info">
                    显示第 {indexOfFirstFile + 1}-{Math.min(indexOfLastFile, filteredFiles.length)} 个文件，共 {filteredFiles.length} 个文件
                  </div>
                  <div className="toolbar-actions">
                    <button 
                      onClick={handleSelectAllFiles}
                      className="secondary-button"
                    >
                      {selectedFiles.length === currentFiles.length && currentFiles.length > 0 ? '取消选择本页' : '选择本页'}
                    </button>
                    <button 
                      onClick={handleSelectAllPages}
                      className="secondary-button"
                    >
                      {selectedFiles.length === filteredFiles.length ? '取消选择全部' : '选择全部'}
                    </button>
                    <button 
                      onClick={downloadBatchAsZip} 
                      disabled={selectedFiles.length === 0 || loading}
                      className="primary-button"
                    >
                      {loading ? '打包中...' : `打包下载 (${selectedFiles.length})`}
                    </button>
                  </div>
                </div>
                
                <div className="files-table-container">
                  <div className="files-table-header">
                    <div className="file-checkbox-header">
                      <input
                        type="checkbox"
                        checked={selectedFiles.length === currentFiles.length && currentFiles.length > 0}
                        onChange={handleSelectAllFiles}
                      />
                    </div>
                    <div className="file-name-header">文件名</div>
                    <div className="file-pubkey-header">公钥</div>
                    <div className="file-date-header">创建时间</div>
                    <div className="file-action-header">操作</div>
                  </div>
                  {currentFiles.map((file) => (
                    <div 
                      key={file.filename} 
                      className={`file-item ${selectedFiles.includes(file.filename) ? 'selected' : ''}`}
                    >
                      <div className="file-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedFiles.includes(file.filename)}
                          onChange={() => handleFileSelect(file.filename)}
                        />
                      </div>
                      <div className="file-name" title={file.filename}>
                        {file.filename}
                      </div>
                      <div className="file-pubkey" title={file.pubkey}>
                        {file.pubkey.substring(0, 8)}...{file.pubkey.substring(file.pubkey.length - 8)}
                      </div>
                      <div className="file-date">
                        {formatTime(file.created)}
                      </div>
                      <div className="file-action">
                        <button 
                          onClick={() => downloadFile(file.filename)}
                          className="download-button"
                        >
                          下载
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {filteredFiles.length === 0 && !loading && (
              <div className="empty-state">
                <p>未找到文件。</p>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* 文件列表分页控件 */}
      {totalFilePages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => paginateFiles(currentPage - 1)} 
            disabled={currentPage === 1}
            className="pagination-button"
          >
            上一页
          </button>
          
          {[...Array(totalFilePages).keys()].map(number => {
            // 当总页数超过3页时，只显示3页：第一页、当前页、最后一页
            if (totalFilePages > 3) {
              // 始终显示第一页
              if (number === 0) {
                return (
                  <button
                    key={number + 1}
                    onClick={() => paginateFiles(number + 1)}
                    className={`pagination-button page-number ${currentPage === number + 1 ? 'active' : ''}`}
                  >
                    {number + 1}
                  </button>
                );
              }
              
              // 当前页左边显示省略号
              if (number === currentPage - 2 && currentPage > 2) {
                return (
                  <span key={`ellipsis-${number}`} className="pagination-ellipsis">
                    ...
                  </span>
                );
              }
              
              // 显示当前页
              if (number === currentPage - 1) {
                return (
                  <button
                    key={number + 1}
                    onClick={() => paginateFiles(number + 1)}
                    className={`pagination-button page-number ${currentPage === number + 1 ? 'active' : ''}`}
                  >
                    {number + 1}
                  </button>
                );
              }
              
              // 当前页右边显示省略号
              if (number === currentPage && currentPage < totalFilePages - 1) {
                return (
                  <span key={`ellipsis-${number}`} className="pagination-ellipsis">
                    ...
                  </span>
                );
              }
              
              // 始终显示最后一页
              if (number === totalFilePages - 1) {
                return (
                  <button
                    key={number + 1}
                    onClick={() => paginateFiles(number + 1)}
                    className={`pagination-button page-number ${currentPage === number + 1 ? 'active' : ''}`}
                  >
                    {number + 1}
                  </button>
                );
              }
              
              return null;
            } else {
              // 总页数不超过3页时，显示所有页码
              return (
                <button
                  key={number + 1}
                  onClick={() => paginateFiles(number + 1)}
                  className={`pagination-button page-number ${currentPage === number + 1 ? 'active' : ''}`}
                >
                  {number + 1}
                </button>
              );
            }
          })}
          
          <button 
            onClick={() => paginateFiles(currentPage + 1)} 
            disabled={currentPage === totalFilePages}
            className="pagination-button"
          >
            下一页
          </button>
        </div>
      )}
    </div>
  );
};

export default FileDownloader;